import { useMemo, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import { computeRunSummary } from '../runSummary';
import type { ConflictsState, ImportRunState } from '../types';

type ConflictsPlaceholderStepProps = {
  portalPath: string;
  mode: 'bulk' | 'single';
  targetCompanyId: number | null;
  run: ImportRunState;
  conflicts: ConflictsState;
  onRetryFetch: () => Promise<void>;
};

const normalizePath = (value: string) =>
  value.length > 1 && value.endsWith('/') ? value.slice(0, -1) : value;

export const ConflictsPlaceholderStep = ({
  portalPath,
  mode: _mode,
  targetCompanyId: _targetCompanyId,
  run,
  conflicts,
  onRetryFetch,
}: ConflictsPlaceholderStepProps) => {
  const [showDetails, setShowDetails] = useState(false);

  const summary = useMemo(() => computeRunSummary(run), [run]);
  const totalConflicts = useMemo(
    () => conflicts.items.reduce((acc, item) => acc + (item.totalItems ?? 0), 0),
    [conflicts.items],
  );
  const hasUnknown = conflicts.items.some((item) => item.status === 'unknown');

  const hasFailures = summary.failed > 0 || summary.stopped > 0;
  const headline = hasFailures
    ? summary.success === 0
      ? __("L'import a échoué")
      : __('Import partiel')
    : __('Import terminé');

  const subline = hasFailures
    ? summary.success === 0
      ? __('Aucun dossier importé.')
      : `${summary.success} ${__('dossier(s) importé(s),')} ${summary.failed + summary.stopped} ${__('en échec.')}`
    : `${summary.success} ${__('dossier(s) importé(s) avec succès.')}`;

  return (
    <div className="import-wizard__outcome">
      <p className="import-wizard__outcome-headline">{headline}</p>
      <p className="import-wizard__outcome-subline">{subline}</p>

      {conflicts.status === 'loading' ? (
        <p className="import-wizard__outcome-loading">{__('Chargement des conflits...')}</p>
      ) : null}

      {!hasFailures && totalConflicts === 0 && conflicts.status !== 'loading' ? (
        <p className="import-wizard__outcome-no-conflicts">{__('Aucun conflit détecté.')}</p>
      ) : null}

      {totalConflicts > 0 ? (
        <p className="import-wizard__outcome-conflicts-count">
          {totalConflicts} {__('conflit(s) à résoudre.')}
        </p>
      ) : null}

      {hasUnknown ? (
        <div role="alert" className="import-wizard__error">
          <p>{__('Certains dossiers ont un nombre de conflits inconnu.')}</p>
          <button type="button" className="btn-secondary" onClick={() => void onRetryFetch()}>
            {__('Relancer la lecture des conflits')}
          </button>
        </div>
      ) : null}

  
      {(hasFailures || conflicts.items.length > 0) ? (
        <div className="import-wizard__outcome-details">
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? __('Masquer les détails') : __('Voir les détails')}
          </button>

          {showDetails ? (
            <div className="import-wizard__outcome-details-body">
              <p>
                {__('Succès')} : {summary.success} &mdash; {__('Échecs')} : {summary.failed} &mdash;{' '}
                {__('Arrêtés')} : {summary.stopped}
              </p>

              {conflicts.items.length > 0 ? (
                <ul className="import-wizard__outcome-conflicts-list">
                  {conflicts.items.map((item) => (
                    <li key={item.companyId}>
                      <span>
                        #{item.companyId} &mdash;{' '}
                        {item.status === 'loaded'
                          ? `${item.totalItems ?? 0} ${__('conflit(s)')}`
                          : __('Conflits inconnus (échec de lecture)')}
                      </span>
                      {' — '}
                      <a href={`${normalizePath(portalPath)}/companies/company/${item.companyId}`}>
                        {__('Voir dossier')}
                      </a>
                      {' — '}
                      <a href={`${normalizePath(portalPath)}/companies/company/${item.companyId}`}>
                        {__('Voir conflits')}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
